import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../core/api';

export default function BaleConnect({ onMessage, onStatus, autoStart = false }) {
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(null); // { code, url, expiresAt }
  const [busy, setBusy] = useState(false);
  const sawAwaiting = useRef(false);
  const autoDone = useRef(false);
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const load = useCallback(async () => {
    try {
      const res = await api.get('/bale');
      setState(res.data);
      onStatusRef.current?.(res.data);
      return res.data;
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در دریافت وضعیت بله', 'danger');
      return null;
    }
  }, [onMessage]);

  useEffect(() => { load(); }, [load]);

  const connect = useCallback(async () => {
    setBusy(true);
    sawAwaiting.current = false;
    try {
      const res = await api.post('/bale/connect');
      setPending(res.data);
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در ساخت کد اتصال', 'danger');
    } finally {
      setBusy(false);
    }
  }, [onMessage]);

  // ساخت خودکار کد (برای صفحه‌ی اجباری)
  useEffect(() => {
    if (!autoStart || autoDone.current || !state) return;
    if (state.enabled && !state.connected && !state.awaitingPhone && !pending) {
      autoDone.current = true;
      connect();
    }
  }, [autoStart, state, pending, connect]);

  // پیگیری وضعیت تا وقتی در جریان اتصال هستیم
  const active = !!pending || !!state?.awaitingPhone;
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(async () => {
      const s = await load();
      if (!s) return;

      if (s.connected) {
        sawAwaiting.current = false;
        setPending(null);
        onMessage?.('شماره تأیید شد و حساب بله متصل شد', 'success');
      } else if (s.awaitingPhone) {
        sawAwaiting.current = true;
      } else if (sawAwaiting.current) {
        // مرحله‌ی شماره دیده شده بود و اتصال حذف شد ← شماره مطابقت نداشت
        sawAwaiting.current = false;
        setPending(null);
        onMessage?.('شماره‌ی بله با شماره‌ی پنل مطابقت نداشت؛ اتصال لغو شد', 'danger');
      } else if (pending && Date.now() > pending.expiresAt) {
        setPending(null);
        onMessage?.('زمان اتصال تمام شد. دوباره تلاش کنید', 'danger');
      }
    }, 3000);
    return () => clearInterval(t);
  }, [active, pending, load, onMessage]);

  const disconnect = async () => {
    if (!window.confirm('اتصال حساب بله قطع شود؟')) return;
    setBusy(true);
    try {
      await api.post('/bale/disconnect');
      setPending(null);
      autoDone.current = true; // بعد از قطع دستی، دوباره خودکار کد نساز
      await load();
      onMessage?.('اتصال بله قطع شد', 'success');
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در قطع اتصال', 'danger');
    } finally {
      setBusy(false);
    }
  };



  if (!state) return null;

  let body;
  if (!state.enabled) {
    body = <div className="muted">بات بله روی سرور تنظیم نشده است.</div>;
  } else if (state.connected) {
    body = (
      <>
        <p className="section-sub" style={{ margin: '0 0 14px' }}>
          متصل به:{' '}
          <b>{state.account?.firstName || state.account?.username || 'حساب بله'}</b>
          {state.account?.username && (
            <span className="badge" style={{ marginInlineStart: 8 }}>@{state.account.username}</span>
          )}
        </p>
     <p className="muted" style={{ margin: '0 0 6px' }}>
  هر شب از ساعت ۰۰:۰۰ یک بکاپ رمزنگاری‌شده از اطلاعات شما در بله ارسال می‌شود.
</p>
        <div className="flexend">
       
          <button type="button" className="btn danger" onClick={disconnect} disabled={busy}>
            <i className="fa-solid fa-link-slash" /> قطع اتصال
          </button>
        </div>
      </>
    );
  } else if (state.awaitingPhone) {
    body = (
      <>
        <p className="section-sub" style={{ margin: '0 0 14px' }}>
          در بله روی دکمه‌ی «ارسال شماره من» بزنید. شماره باید با شماره‌ی ورود شما به پنل یکی باشد.
        </p>
        <div className="muted">
          <i className="fa-solid fa-spinner fa-spin" /> در انتظار تأیید شماره...
        </div>
        <div className="flexend">
          <button type="button" className="btn secondary" onClick={disconnect} disabled={busy}>
            انصراف
          </button>
        </div>
      </>
    );
  } else if (pending) {
    body = (
      <>
        <p className="section-sub" style={{ margin: '0 0 14px' }}>
          دکمه‌ی زیر را بزنید و در بله روی «شروع» بزنید. اگر باز نشد، این کد را برای بات بفرستید:
        </p>
        <div className="bale-code">{pending.code}</div>
        <div className="flexend">
          <a className="btn" href={pending.url} target="_blank" rel="noopener noreferrer">
            <i className="fa-solid fa-paper-plane" /> باز کردن بله
          </a>
        </div>
        <div className="muted" style={{ marginTop: 10 }}>
          <i className="fa-solid fa-spinner fa-spin" /> در انتظار اتصال... (کد ۱۰ دقیقه اعتبار دارد)
        </div>
      </>
    );
  } else {
    body = (
      <>
        <p className="section-sub" style={{ margin: '0 0 14px' }}>
          با اتصال حساب بله، بکاپ شبانه و کد بازیابی رمز برای شما ارسال می‌شود. شماره‌ی بله باید با شماره‌ی ورود به پنل یکی باشد.
        </p>
        <div className="flexend" style={{ marginTop: 0 }}>
          <button type="button" className="btn" onClick={connect} disabled={busy}>
            <i className="fa-solid fa-link" /> اتصال به بله
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="card">
      <h2>اتصال به بله</h2>
      {body}
    </div>
  );
}